/**
 * SADARayon — Section "Notifications & Devices" (admin/manager).
 *
 * Affiche la liste des téléphones abonnés aux alertes push SADARayon avec :
 *   - Date d'inscription
 *   - Dernière activité (last_seen)
 *   - Label de l'appareil (UA tronqué)
 *   - Utilisateur ayant abonné
 *   - Bouton « Désactiver ce device »
 *
 * Source : GET /api/inventory/push/subscriptions (admin only)
 *          DELETE /api/inventory/push/subscriptions/{id}
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const summarizeDevice = (label) => {
  if (!label) return 'Appareil inconnu';
  const s = String(label);
  if (/iPhone/i.test(s)) return 'iPhone';
  if (/iPad/i.test(s)) return 'iPad';
  if (/Android/i.test(s)) {
    const m = s.match(/Android[^;)]*[;)]\s*([^;)]+)/);
    return m ? `Android · ${m[1].trim()}` : 'Android';
  }
  if (/Macintosh/i.test(s)) return 'Mac';
  if (/Windows/i.test(s)) return 'Windows';
  if (/Linux/i.test(s)) return 'Linux';
  return s.slice(0, 40) + (s.length > 40 ? '…' : '');
};

const InventoryDevicesAdmin = () => {
  const [items, setItems] = useState([]);
  const [lastBroadcast, setLastBroadcast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/push/subscriptions`, { headers: auth() });
      setItems(res.data?.items || []);
      setLastBroadcast(res.data?.last_broadcast_at || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (sub) => {
    if (!window.confirm(`Désactiver les alertes sur "${summarizeDevice(sub.device_label)}" (${sub.user_name}) ?\n\nCe téléphone ne recevra plus aucune notif SADARayon tant qu'il n'aura pas réactivé l'abonnement.`)) {
      return;
    }
    setDeleting(sub.id);
    try {
      await axios.delete(`${API}/inventory/push/subscriptions/${sub.id}`, { headers: auth() });
      toast.success('Device désactivé.');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="pilotage-devices">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[#1B4D3E]" />
          <h3 className="font-medium text-zinc-900">Téléphones abonnés aux alertes</h3>
          <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200">{items.length}</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          {lastBroadcast && <span>Dernière diffusion : {formatDateTime(lastBroadcast)}</span>}
          <Button
            size="sm" variant="outline"
            onClick={reload}
            disabled={loading}
            data-testid="pilotage-devices-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-600">
              <th className="px-3 py-2 font-medium">Appareil</th>
              <th className="px-3 py-2 font-medium">Utilisateur</th>
              <th className="px-3 py-2 font-medium">Abonné le</th>
              <th className="px-3 py-2 font-medium">Dernière activité</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sub) => (
              <tr key={sub.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`pilotage-device-row-${sub.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-zinc-900">{summarizeDevice(sub.device_label)}</div>
                  <div className="text-xs text-zinc-400" title={sub.device_label || ''}>{(sub.device_label || '').slice(0, 60)}</div>
                </td>
                <td className="px-3 py-2 text-zinc-700">
                  {sub.user_name || <span className="text-zinc-400">—</span>}
                  {sub.user_role && (
                    <span className="ml-2 text-xs uppercase tracking-wide text-zinc-500">{sub.user_role}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{formatDateTime(sub.created_at)}</td>
                <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{formatDateTime(sub.last_seen)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(sub)}
                    disabled={deleting === sub.id}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`pilotage-device-delete-${sub.id}`}
                  >
                    {deleting === sub.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                    Désactiver
                  </Button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-zinc-500" data-testid="pilotage-devices-empty">
                  {loading ? 'Chargement…' : 'Aucun téléphone abonné pour le moment.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryDevicesAdmin;
